import React, { useState, useEffect } from 'react';
import * as metricsService from '../services/metricsService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Spinner from '../components/Spinner';
import PageTitle from '../components/PageTitle';

// Import tab components
import OverviewTab from './metrics/OverviewTab';
import MessagesTab from './metrics/MessagesTab';
import CacheTab from './metrics/CacheTab';
import ProjectsTab from './metrics/ProjectsTab';
import KeywordsTab from './metrics/KeywordsTab';
import MemoryMonitorTab from './metrics/MemoryMonitorTab';
import MemoryDiagnosticsTab from './metrics/MemoryDiagnosticsTab';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Metrics = () => {
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [hourlyMetrics, setHourlyMetrics] = useState([]);
  const [dailyMetrics, setDailyMetrics] = useState([]);
  const [weeklyMetrics, setWeeklyMetrics] = useState([]);
  const [projectStats, setProjectStats] = useState([]);
  const [keywordMetrics, setKeywordMetrics] = useState(null);
  const [memoryStats, setMemoryStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeTimeRange, setActiveTimeRange] = useState('hourly');
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [gcTriggering, setGcTriggering] = useState(false);

  const fetchData = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch current metrics (server-side caching handles efficiency)
      const currentRes = await metricsService.getCurrentMetrics();
      if (currentRes.success && currentRes.metrics) {
        setCurrentMetrics(currentRes.metrics);
      }

      // Only fetch other data if on the relevant tab or initial load
      if (!isManualRefresh || activeTab === 'overview' || activeTab === 'messages') {
        // Fetch historical metrics with proper error handling
        const hourlyRes = await metricsService.getHistoricalMetrics('hourly', 24);
        if (hourlyRes.success && Array.isArray(hourlyRes.metrics)) {
          setHourlyMetrics(hourlyRes.metrics.length > 0
            ? hourlyRes.metrics.reverse() // Reverse to show oldest to newest
            : []);
        } else {
          setHourlyMetrics([]);
        }
      }

      if (!isManualRefresh || activeTab === 'overview') {
        const dailyRes = await metricsService.getHistoricalMetrics('daily', 30);
        if (dailyRes.success && Array.isArray(dailyRes.metrics)) {
          setDailyMetrics(dailyRes.metrics.length > 0
            ? dailyRes.metrics.reverse()
            : []);
        } else {
          setDailyMetrics([]);
        }

        const weeklyRes = await metricsService.getHistoricalMetrics('weekly', 12);
        if (weeklyRes.success && Array.isArray(weeklyRes.metrics)) {
          setWeeklyMetrics(weeklyRes.metrics.length > 0
            ? weeklyRes.metrics.reverse()
            : []);
        } else {
          setWeeklyMetrics([]);
        }
      }

      if (!isManualRefresh || activeTab === 'projects') {
        // Fetch project stats
        const projectsRes = await metricsService.getProjectStats(10);
        if (projectsRes.success && Array.isArray(projectsRes.stats)) {
          setProjectStats(projectsRes.stats);
        } else {
          setProjectStats([]);
        }
      }

      if (!isManualRefresh || activeTab === 'keywords') {
        // Fetch keyword metrics
        const keywordsRes = await metricsService.getKeywordMetrics();
        if (keywordsRes.success && keywordsRes.metrics) {
          setKeywordMetrics(keywordsRes.metrics);
        } else {
          setKeywordMetrics(null);
        }
      }

      setLastUpdate(new Date());
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      if (error.response && error.response.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to load metrics. Please try again later.');
      }
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Set up interval to refresh data every 5 minutes (reduced from 1 minute)
    const intervalId = setInterval(() => fetchData(), 300000); // 5 minutes

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Fetch additional data when switching tabs (lazy loading)
  useEffect(() => {
    if (!loading && activeTab === 'projects' && projectStats.length === 0) {
      metricsService.getProjectStats(10)
        .then(res => {
          if (res.success && Array.isArray(res.stats)) {
            setProjectStats(res.stats);
          }
        })
        .catch(err => console.error('Error loading project stats:', err));
    }

    if (!loading && activeTab === 'keywords' && !keywordMetrics) {
      metricsService.getKeywordMetrics()
        .then(res => {
          if (res.success && res.metrics) {
            setKeywordMetrics(res.metrics);
          }
        })
        .catch(err => console.error('Error loading keyword metrics:', err));
    }

    if (!loading && activeTab === 'memory' && !memoryStats) {
      metricsService.getMemoryStats(true)
        .then(res => {
          if (res.success && res.data) {
            setMemoryStats(res.data);
          }
        })
        .catch(err => console.error('Error loading memory stats:', err));
    }
  }, [activeTab, loading, projectStats.length, keywordMetrics, memoryStats]);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchData(true);
  };

  // Trigger garbage collection
  const handleGarbageCollection = async () => {
    try {
      setGcTriggering(true);
      const result = await metricsService.triggerGarbageCollection();
      if (result.success) {
        // Refresh memory stats after GC
        const memRes = await metricsService.getMemoryStats(true);
        if (memRes.success) {
          setMemoryStats(memRes.data);
        }
        alert('Garbage collection completed successfully!');
      } else {
        alert('Failed to trigger garbage collection: ' + result.error);
      }
    } catch (error) {
      console.error('Error triggering GC:', error);
      alert('Error triggering garbage collection: ' + error.message);
    } finally {
      setGcTriggering(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <PageTitle title="System Metrics" />
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          <button
            className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
            onClick={() => fetchData()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <PageTitle title="System Metrics" />
        <div className="flex items-center space-x-4">
          {lastUpdate && (
            <span className="text-sm text-gray-500">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="bg-blue-500 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-2 px-4 rounded flex items-center"
          >
            {refreshing ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Refreshing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="mb-4 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'messages'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('messages')}
            >
              Message Processing
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'cache'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('cache')}
            >
              Cache Performance
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'projects'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('projects')}
            >
              Projects
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'keywords'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('keywords')}
            >
              Keywords
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'memory'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('memory')}
            >
              Memory Monitor
            </button>
          </li>
          <li>
            <button
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'diagnostics'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('diagnostics')}
            >
              Memory Diagnostics
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab currentMetrics={currentMetrics} hourlyMetrics={hourlyMetrics} />
      )}

      {activeTab === 'messages' && (
        <MessagesTab currentMetrics={currentMetrics} hourlyMetrics={hourlyMetrics} />
      )}

      {activeTab === 'cache' && (
        <CacheTab currentMetrics={currentMetrics} />
      )}

      {activeTab === 'projects' && (
        <ProjectsTab projectStats={projectStats} />
      )}

      {activeTab === 'keywords' && (
        <KeywordsTab keywordMetrics={keywordMetrics} />
      )}

      {activeTab === 'memory' && (
        <MemoryMonitorTab 
          memoryStats={memoryStats} 
          gcTriggering={gcTriggering} 
          handleGarbageCollection={handleGarbageCollection} 
        />
      )}

      {activeTab === 'diagnostics' && (
        <MemoryDiagnosticsTab />
      )}
    </div>
  );
};

export default Metrics;
